import { Check, ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/contexts/AuthContext";
import { publicCheckoutLink } from "@/lib/publicCheckout";
import { HOME } from "@/constants/testIds";
import { usePresaleDialog } from "@/components/sections/PresaleDialog";



const plans = [
  {
    name: "Starter",
    plan_key: "Starter",
    price: "€499",
    price_annual: "€4,990",
    suffix: "/mo",
    suffix_annual: "/yr",
    desc: "Core business tools for small teams.",
    features: [
      "AI assistants, planning & communication",
      "CRM, projects & basic finance",
      "Email & social marketing",
      "Website Builder · 100 templates",
      "1 workspace · 8 users · 8 emails",
      "No full ERP",
    ],
    cta: "Subscribe to Starter",
  },
  {
    name: "Creator",
    plan_key: "Creator",
    price: "€699",
    price_annual: "€6,990",
    suffix: "/mo",
    suffix_annual: "/yr",
    desc: "Full creative and sales tools for growing teams.",
    features: [
      "Everything in Starter",
      "Full AI creative suite",
      "Full CRM, sales & finance",
      "Website Builder · 100 templates",
      "1 workspace · 15 users · 15 emails",
      "No full ERP",
    ],
    cta: "Subscribe to Creator",
  },
  {
    name: "Business",
    plan_key: "Business",
    price: "€899",
    price_annual: "€8,990",
    suffix: "/mo",
    suffix_annual: "/yr",
    desc: "Complete operations for growing SMEs.",
    features: [
      "Everything in Creator",
      "Full projects, accounting & operations",
      "Advanced team workflows",
      "Website Builder · 100 templates",
      "3 workspaces · 25 users · unlimited emails",
      "No full ERP",
    ],
    cta: "Subscribe to Business",
    popular: true,
  },
  {
    name: "Agency",
    plan_key: "Agency",
    price: "€1,199",
    price_annual: "€11,990",
    suffix: "/mo",
    suffix_annual: "/yr",
    desc: "Full multi-client operations for agencies.",
    features: [
      "Everything in Business",
      "Multi-client projects & marketing",
      "Multi-brand team workflows",
      "Website Builder · 100 templates",
      "5 workspaces · 50 users · unlimited emails",
      "No full ERP",
    ],
    cta: "Subscribe to Agency",
  },
  {
    name: "Enterprise",
    plan_key: "Enterprise Basic",
    price: "from €2,499",
    price_annual: "from €24,990",
    suffix: "/mo",
    suffix_annual: "/yr",
    desc: "All 12 domains, full ERP and enterprise controls.",
    features: [
      "All 12 domains · full ERP",
      "Unlimited users",
      "Workspaces by Enterprise tier",
      "SSO, audit trail, security policies",
      "Dedicated support & onboarding",
    ],
    cta: "Talk to Sales",
    enterprise: true,
  },
];

export default function Pricing() {
  const { openDialog } = usePresaleDialog();
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState({});
  const [annual, setAnnual] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [checkoutPending, setCheckoutPending] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/pricing/catalog`);
        if (!active) return;
        const map = {};
        (data?.plans || []).forEach((p) => { map[p.plan_key] = p; });
        setCatalog(map);
      } catch {
        /* graceful: keep CTA as presale */
      }
    })();
    return () => { active = false; };
  }, []);

  const onCta = async (plan) => {
    const link = publicCheckoutLink(catalog[plan.plan_key], annual ? "year" : "month");
    if (plan.enterprise) {
      navigate("/pricing#enterprise");
      return;
    }
    // Referral attribution requires a Stripe Session we control, not a static Payment Link.
    // Fail closed: never silently discard an affiliate referral when checkout fails.
    const marketingAllowed = (() => {
      try {
        return JSON.parse(localStorage.getItem("zy_cookie_prefs_v1") || "null")?.marketing === true &&
          window.__zyMarketingDeniedForPage !== true;
      } catch { return false; }
    })();
    const cookieTid = document.cookie.split("; ").find((part) => part.startsWith("_fprom_tid="));
    const tid = marketingAllowed
      ? (typeof window.FPROM?.data?.tid === "string"
          ? window.FPROM.data.tid
          : cookieTid ? decodeURIComponent(cookieTid.slice("_fprom_tid=".length)) : null)
      : null;
    if (!annual && tid && /^[A-Za-z0-9_-]{1,128}$/.test(tid)) {
      setCheckoutPending(true);
      setCheckoutError("");
      try {
        const { data } = await axios.post(`${API}/checkout/public-referral/session`, {
          plan_key: plan.plan_key, fp_tid: tid,
        });
        if (!data?.url || !/^https:\/\/checkout\.stripe\.com\//.test(data.url)) {
          throw new Error("Invalid Stripe checkout URL");
        }
        window.location.assign(data.url);
      } catch {
        setCheckoutError("Affiliate checkout is temporarily unavailable. Please try again later.");
      } finally {
        setCheckoutPending(false);
      }
      return;
    }
    if (link) {
      window.location.href = link;
      return;
    }
    if (annual) {
      window.location.href = `mailto:info@zynthoro.ai?subject=${encodeURIComponent(`${plan.name} annual subscription`)}`;
      return;
    }
    // Fallback while the monthly catalog is loading or unavailable.
    openDialog();
  };

  return (
    <section id="pricing" data-testid={HOME.pricing} className="zy-section bg-white">
      <div className="zy-container">
        <div className="max-w-3xl mx-auto text-center mb-10 zy-reveal">
          <p className="zy-eyebrow mb-4">Pricing</p>
          <h2 className="zy-h2">Simple, transparent pricing. No surprises.</h2>
          <p className="zy-body mt-5">
            Founding member pricing locked for life when you join the presale.
          </p>
        </div>

        {/* Monthly / Annual toggle (2 months free on annual) */}
        <div className="flex justify-center mb-10 zy-reveal">
          <div
            role="tablist"
            aria-label="Billing period"
            className="inline-flex items-center gap-1 p-1 rounded-full border border-[#e5e7ee] bg-white shadow-sm"
            data-testid="pricing-billing-toggle"
          >
            <button
              type="button"
              role="tab"
              aria-selected={!annual}
              onClick={() => setAnnual(false)}
              className={`px-4 py-1.5 rounded-full text-[13.5px] font-medium transition-colors ${
                !annual ? "bg-[#202B60] text-white" : "text-[#333] hover:text-black"
              }`}
              data-testid="pricing-toggle-monthly"
            >
              Monthly
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={annual}
              onClick={() => setAnnual(true)}
              className={`px-4 py-1.5 rounded-full text-[13.5px] font-medium transition-colors flex items-center gap-2 ${
                annual ? "bg-[#202B60] text-white" : "text-[#333] hover:text-black"
              }`}
              data-testid="pricing-toggle-annual"
            >
              Annual
              <span
                className={`text-[10.5px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider ${
                  annual ? "bg-white/25 text-white" : "bg-[#E8FFE9] text-[#0F7A2A]"
                }`}
              >
                2 months free
              </span>
            </button>
          </div>
        </div>

        {checkoutError && <p role="alert" className="mb-5 text-center text-red-700">{checkoutError}</p>}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {plans.map((p, i) => (
            <div
              key={p.name}
              data-testid={`pricing-card-${p.name.toLowerCase()}`}
              className={`zy-price-card zy-reveal ${p.popular ? "popular" : ""} ${p.enterprise ? "enterprise" : ""}`}
              style={{ transitionDelay: `${i * 70}ms` }}
            >
              {p.popular && (
                <span
                  className="absolute -top-3 left-6 text-[11px] font-bold tracking-wide uppercase px-3 py-1 rounded-full text-white"
                  style={{ background: "var(--zy-blue)" }}
                >
                  Most Popular
                </span>
              )}
              {p.enterprise && (
                <span
                  className="absolute -top-3 left-6 text-[11px] font-bold tracking-wide uppercase px-3 py-1 rounded-full"
                  style={{ background: "var(--zy-gold)", color: "#1a1300" }}
                >
                  Enterprise
                </span>
              )}

              <h3 className="zy-h3 text-[1.05rem]">{p.name}</h3>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-[28px] font-bold tracking-tight text-black" data-testid={`pricing-card-${p.name.toLowerCase()}-price`}>
                  {annual ? (p.price_annual || p.price) : p.price}
                </span>
                <span className="text-[#666] text-[14px]">
                  {annual ? (p.suffix_annual || p.suffix) : p.suffix}
                </span>
              </div>
              <p className="text-[13.5px] text-[#555] mt-2 leading-relaxed min-h-[40px]">{p.desc}</p>

              <ul className="mt-5 space-y-2.5 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-[13.5px] text-[#333]">
                    <Check size={14} className="mt-0.5 shrink-0" style={{ color: "var(--zy-blue)" }} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => onCta(p)}
                disabled={checkoutPending}
                className={p.enterprise ? "zy-btn-gold mt-6 w-full" : "zy-btn-primary mt-6 w-full"}
                style={{ fontSize: 14, padding: "12px 18px" }}
                data-testid={`pricing-cta-${p.name.toLowerCase()}`}
              >
                {annual && !p.enterprise && !publicCheckoutLink(catalog[p.plan_key], "year")
                  ? "Request annual plan" : p.cta}
              </button>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center zy-reveal">
          <a href="#enterprise" className="zy-link">
            See enterprise features <ArrowRight size={16} />
          </a>
        </div>
      </div>
    </section>
  );
}

